/**
 * Interaction reporter — batches recycle-kiosk interaction outcomes and POSTs
 * them to the CMS `/api/interactions` endpoint.
 *
 * Mirrors the play-log tracker's POST/flush pattern (see
 * `apps/player/src/playlog/usePlayLog.ts`): buffer completed entries in a
 * ref, flush on an interval + unmount, and retain the buffer for retry when
 * a flush fails. Also mirrors its durability pattern: a page-hide flush uses
 * `keepalive: true` fetch (sendBeacon can't carry the Authorization header
 * the CMS requires) and swap-and-sends the buffer so REBOOT/CLEAR_CACHE
 * reloads and tab-close don't silently drop buffered outcomes.
 *
 * This hook is deliberately agnostic to *what* gets reported and *when* —
 * that decision lives in `interaction-outcomes.ts`. In particular, a pending
 * OBSTRUCTED interaction (an obstruction the machine tracked but never saw
 * resolve to a terminal `result`) is flushed exactly once when contract v2's
 * `recovery_complete` event resets the recycle-machine back to `idle` — see
 * `recycle-machine.ts`'s `recovery_complete` case and
 * `decideOutcomeOnIdleReset` in `interaction-outcomes.ts`. There is no
 * recovery-specific code in this hook; `report()` is called generically by
 * the composition root's (`App.tsx`, or `integration.test.tsx`'s `Harness`
 * for tests) idle-transition effect the same way for any idle reset.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { InteractionEntryInput } from '@dooh/shared';
import { CMS_API_BASE } from '../types';

/** Batch interval in milliseconds (60 s). */
const BATCH_INTERVAL_MS = 60 * 1000;

/** Maximum entries to keep in memory (safety cap). */
const MAX_BUFFER_SIZE = 200;

export interface UseInteractionReporterOptions {
  deviceId: string;
  screenId: string;
  /** JWT bearer token for authenticating with the CMS interactions API. */
  jwt: string;
  /** Optional: flush interval override in ms (default: 60000 = 60 s). */
  flushIntervalMs?: number;
}

export interface UseInteractionReporterReturn {
  /** Buffer an interaction entry; it is sent on the next flush. */
  report: (entry: InteractionEntryInput) => void;
  /** Manually flush buffered interactions to the CMS. */
  flush: () => Promise<void>;
}

function log(...args: unknown[]) {
  // Dev-only: silent in production kiosk builds (see useRecycleAgent).
  if (import.meta.env.DEV) console.log('[InteractionReporter]', ...args);
}

/**
 * POST a batch of interaction entries to the CMS API.
 */
async function postInteractionBatch(
  deviceId: string,
  screenId: string,
  jwt: string,
  interactions: InteractionEntryInput[],
): Promise<boolean> {
  if (interactions.length === 0) return true;

  try {
    const response = await fetch(`${CMS_API_BASE}/api/interactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ deviceId, screenId, interactions }),
    });

    if (response.ok) {
      const result = await response.json();
      log(`Flushed ${result.ingested ?? interactions.length} interactions to CMS`);
      return true;
    }

    const errorText = await response.text().catch(() => 'Unknown error');
    log(`Failed to flush interactions: ${response.status} ${errorText}`);
    return false;
  } catch (err) {
    log(`Network error flushing interactions: ${err instanceof Error ? err.message : err}`);
    return false;
  }
}

/**
 * Send a batch of interactions using a keepalive fetch (for page unload).
 *
 * navigator.sendBeacon cannot set custom headers, so it can't carry the
 * Authorization token the CMS requires. A keepalive fetch can set headers
 * and survives page unload in Chromium (the player runtime), making it the
 * correct primitive for this fire-and-forget send.
 */
async function sendKeepaliveBatch(
  deviceId: string,
  screenId: string,
  jwt: string,
  interactions: InteractionEntryInput[],
): Promise<boolean> {
  if (interactions.length === 0) return true;

  try {
    const response = await fetch(`${CMS_API_BASE}/api/interactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ deviceId, screenId, interactions }),
      keepalive: true,
    });
    return response.ok;
  } catch (err) {
    log(`Network error sending keepalive interactions: ${err instanceof Error ? err.message : err}`);
    return false;
  }
}

/**
 * React hook for batching and reporting recycle-kiosk interaction outcomes.
 */
export function useInteractionReporter(
  options: UseInteractionReporterOptions,
): UseInteractionReporterReturn {
  const { deviceId, screenId, jwt, flushIntervalMs = BATCH_INTERVAL_MS } = options;

  const bufferRef = useRef<InteractionEntryInput[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const flush = useCallback(async () => {
    const entries = bufferRef.current;
    if (entries.length === 0) {
      log('No interactions to flush');
      return;
    }

    // Take entries from buffer
    bufferRef.current = [];

    const success = await postInteractionBatch(deviceId, screenId, jwt, entries);

    if (!success) {
      // Put entries back at the beginning of the buffer for retry
      bufferRef.current = [...entries, ...bufferRef.current];
      log(`Retaining ${bufferRef.current.length} interactions for next flush`);
    }
  }, [deviceId, screenId, jwt]);

  const report = useCallback((entry: InteractionEntryInput) => {
    bufferRef.current.push(entry);
    if (bufferRef.current.length > MAX_BUFFER_SIZE) {
      bufferRef.current = bufferRef.current.slice(-MAX_BUFFER_SIZE);
    }
    log(`Buffered interaction ${entry.txnId} (${entry.outcome})`);
  }, []);

  // ─── Flush on interval ──────────────────────────────────────────────
  useEffect(() => {
    flushTimerRef.current = setInterval(() => {
      flush();
    }, flushIntervalMs);

    return () => {
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [flush, flushIntervalMs]);

  // ─── Flush on visibility change / unload / unmount ───────────────────
  //
  // These handlers can fire together on page hide (visibilitychange→hidden +
  // beforeunload + React unmount). Mirrors usePlayLog's dedup: swap the
  // buffer out atomically before sending so a concurrent handler sees an
  // empty buffer and skips.

  /**
   * Atomically flush the buffer: swap-and-send. Returns true if sent.
   * Uses a swap pattern so concurrent handlers can't double-send.
   */
  const flushBeacon = useCallback((): boolean => {
    const entries = bufferRef.current;
    if (entries.length === 0) return false;

    // Swap: clear the ref BEFORE sending, so a concurrent handler can't
    // re-send the same entries.
    const entriesToSend = entries.splice(0, entries.length);

    // Fire the keepalive send. Because sendKeepaliveBatch is async and the
    // buffer was already swapped above, a concurrent unload handler will see
    // an empty buffer and skip — preserving the dedup guarantee.
    void sendKeepaliveBatch(deviceId, screenId, jwt, entriesToSend).then((success) => {
      if (success) {
        log(`Flushed ${entriesToSend.length} interactions via keepalive fetch`);
      } else {
        // Send failed — push entries back for a future flush attempt
        bufferRef.current.unshift(...entriesToSend);
      }
    });
    return true;
  }, [deviceId, screenId, jwt]);

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        flushBeacon();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [flushBeacon]);

  useEffect(() => {
    function handleBeforeUnload() {
      flushBeacon();
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushBeacon]);

  // ─── Flush on unmount ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      flushBeacon();
    };
  }, [flushBeacon]);

  // Stable identity across renders so consumers (e.g. App.tsx effect deps)
  // don't need to suppress exhaustive-deps or re-run on every render.
  return useMemo(() => ({ report, flush }), [report, flush]);
}
